import { FormEvent, useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Loader2, Mail, User as UserIcon } from 'lucide-react'
import { AuthShell } from '@/components/AuthShell'
import LogoSpinner from '@/components/ui/LogoSpinner'
import { apiService } from '@/services/apiService'
import { useAuthStore } from '@/stores/authStore'
import { hasRequiredMemberName } from '@/utils/memberName'

const CompleteProfilePage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { status, isLoading, user, supabaseUser, updateUser } = useAuthStore()
  const [firstName, setFirstName] = useState(user?.firstName ?? '')
  const [lastName, setLastName] = useState(user?.lastName ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setFirstName(user?.firstName ?? '')
    setLastName(user?.lastName ?? '')
  }, [user?.id, user?.firstName, user?.lastName])

  if (isLoading || status === 'unknown') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-deepwater">
        <LogoSpinner size="lg" label="Loading your profile" />
      </div>
    )
  }

  if (status === 'anonymous' || status === 'denied') {
    return <Navigate to="/login" replace />
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-deepwater">
        <LogoSpinner size="lg" label="Loading your profile" />
      </div>
    )
  }

  const passwordSet = supabaseUser?.user_metadata?.['passwordSet'] === true
  if (!passwordSet) {
    return <Navigate to="/set-password" replace />
  }

  if (hasRequiredMemberName(user)) {
    return <Navigate to="/dashboard" replace />
  }

  const returnTo =
    typeof location.state === 'object' &&
    location.state !== null &&
    'returnTo' in location.state &&
    typeof location.state.returnTo === 'string'
      ? location.state.returnTo
      : '/dashboard'

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    const trimmedFirst = firstName.trim()
    const trimmedLast = lastName.trim()
    if (!trimmedFirst || !trimmedLast) {
      setError('First name and last name are required.')
      return
    }

    setSaving(true)
    try {
      const updated = await apiService.updateProfile({
        firstName: trimmedFirst,
        lastName: trimmedLast,
      })
      updateUser({
        firstName: updated.firstName,
        lastName: updated.lastName,
      })
      toast.success('Profile completed.')
      navigate(returnTo, { replace: true })
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Could not save your profile. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <AuthShell
      title="Complete your profile"
      subtitle="Add your name before continuing to the portal"
      backTo="/"
      backLabel="Back"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <p className="text-sm text-blue-50/90 leading-relaxed">
          Your email stays tied to sign-in. Your name is what neighbors see in the directory,
          bulletin board, messages, and member pages.
        </p>

        <div>
          <span className="block text-sm font-medium text-white mb-2">Email address</span>
          <div className="flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-3 text-sm text-blue-50">
            <Mail size={16} className="text-blue-200 shrink-0" />
            <span className="truncate">{user.email}</span>
          </div>
        </div>

        <label className="block">
          <span className="block text-sm font-medium text-white mb-2">First Name</span>
          <div className="relative">
            <UserIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              autoComplete="given-name"
              required
              autoFocus
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value)
                if (error) setError('')
              }}
              className="w-full pl-10 pr-4 py-3 bg-white/95 border border-gray-200 rounded-lg input-focus text-gray-900 placeholder-gray-400"
            />
          </div>
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-white mb-2">Last Name</span>
          <div className="relative">
            <UserIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              autoComplete="family-name"
              required
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value)
                if (error) setError('')
              }}
              className="w-full pl-10 pr-4 py-3 bg-white/95 border border-gray-200 rounded-lg input-focus text-gray-900 placeholder-gray-400"
            />
          </div>
        </label>

        {error && (
          <div className="bg-red-500/15 border border-red-300/40 text-red-100 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={saving}
          className="w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-full bg-white text-blue-900 font-semibold hover:bg-blue-50 transition disabled:opacity-60 disabled:cursor-not-allowed shadow-lg"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save and continue'
          )}
        </button>
      </form>
    </AuthShell>
  )
}

export default CompleteProfilePage
